import React from "react";
import styled, { keyframes } from "styled-components";
import {
  FaBolt,
  FaShieldAlt,
  FaUsers,
  FaCogs,
  FaThumbsUp,
  FaRocket,
} from "react-icons/fa";

const gradientBG = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const Section = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #0d0d0d, #1a1a1a);
  text-align: center;
`;

const Title = styled.h2`
  font-size: 2.8rem;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4rem;
`;
const Timeline = styled.div`
  position: relative;
  max-width: 1000px;
  margin: auto;

  &::after {
    content: "";
    position: absolute;
    width: 4px;
    background: repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent 10px,
      #ff7a18 10px,
      #ff7a18 20px,
      #ff0066 20px,
      #ff0066 30px
    );
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -2px;
    z-index: 0;

    @media (max-width: 768px) {
      left: 20px; /* move to left edge */
    }
  }
`;

const StepWrapper = styled.div`
  padding: 2rem 2rem;
  position: relative;
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
    padding-left: 3.5rem;
    padding-right: 1rem;
    left: 0 !important;
    box-sizing: border-box;
  }
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  max-width: 100%; /* ✅ Never overflow */

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 25px rgba(255, 122, 24, 0.3);
  }

  @media (max-width: 768px) {
    text-align: center;
    padding: 1.2rem 1rem;
    border-radius: 12px;
  }
`;
const StepCount = styled.div`
  position: absolute;
  top: 20px;
  ${({ side }) => (side === "left" ? "right: -18px;" : "left: -18px;")}
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: #fff;
  font-weight: bold;
  font-size: 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 0 10px rgba(255, 122, 24, 0.6);

  @media (max-width: 768px) {
    left: -40px !important; /* ✅ keep inside screen */
    right: auto !important;
  }
`;

const StepIcon = styled.div`
  font-size: 2.5rem;
  color: #ff7a18;
  margin-bottom: 1.2rem;
  transition: color 0.3s ease;

  ${StepCard}:hover & {
    color: #ff0066;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.3rem;
  color: #fff;
  margin-bottom: 0.6rem;
`;

const StepDesc = styled.p`
  color: #ccc;
  font-size: 0.95rem;
`;

const steps = [
  {
    icon: <FaUsers />,
    title: "1. Understanding Your Needs",
    desc: "We begin with a detailed discussion of your goals, challenges, and requirements.",
  },
  {
    icon: <FaCogs />,
    title: "2. Planning & Strategy",
    desc: "We create a clear roadmap, define milestones, and finalize the technology stack.",
  },
  {
    icon: <FaBolt />,
    title: "3. Initial Payment & Kickoff",
    desc: "To get started, we require a 20% upfront payment. This ensures commitment and allows us to begin immediately.",
  },
  {
    icon: <FaShieldAlt />,
    title: "4. Development & Milestone Delivery",
    desc: "Work is executed in phases. Each milestone is reviewed with you, followed by milestone-based payments.",
  },
  {
    icon: <FaThumbsUp />,
    title: "5. Review & Feedback",
    desc: "We present completed modules for your feedback and refine until you’re satisfied.",
  },
  {
    icon: <FaRocket />,
    title: "6. Final Delivery & Ongoing Support",
    desc: "Once all milestones are completed and final payment is made, we launch your project and provide continuous support.",
  },
];

export default function WhyChooseUs() {
  return (
    <Section>
      <Title>How We Work as Freelancer ?</Title>
      <Timeline>
        {steps.map((step, index) => {
          const isLeft = index % 2 === 0;
          return (
            <StepWrapper
              key={index}
              style={{
                left: isLeft ? "0" : "50%",
              }}
            >
              <StepCard>
                <StepCount side={isLeft ? "left" : "right"}>
                  {index + 1}
                </StepCount>
                <StepIcon>{step.icon}</StepIcon>
                <StepTitle>{step.title}</StepTitle>
                <StepDesc>{step.desc}</StepDesc>
              </StepCard>
            </StepWrapper>
          );
        })}
      </Timeline>
    </Section>
  );
}
